/**
 * evacuationWindowModel.js - 2026-10-19 (docs/research/EVACUATION_DELAY_FACTOR_TABLE.md); wired into the wound hazard model 2026-10-19 (EVAC_WAIT_DERIVED_ON in casualtyCalculator.js)
 *
 * Dr. Tatar's decision (docs/TODO_CURRENT.md R2-23): the wait before the first movement of a casualty (the "evacuation hold") is not typed by
 * the planner. It follows from the conditions of the operation, as a spread.
 *
 * Model. On a given day the evacuation window is OPEN (a ground or air evacuation can move at once: the wait is about zero, the travel time is
 * the one of the transit legs) with probability p_open, or CLOSED. When it is closed the casualty waits until it reopens; the closure lasts a
 * random time, exponential with mean D hours. So the wait W has the distribution
 *     P(W = 0) = p_open,   P(W > t) = (1 - p_open) * exp(-t / D)   for t > 0.
 * p_open = 1 - (1 - p_ground) * (1 - p_air): either mode moving is enough.
 *   p_ground = road availability (resolveRoadState: LOC security, bridge, MSR / ASR, IED, insurgent, weather) x drone x intensity x artillery
 *   p_air    = helicopter availability (air situation ceiling x enemy air defense x weather) x drone x intensity
 *   D        = D0 * (1 + K * (1 - p_open)): the more closed the window, the longer it stays closed.
 * Status of the numbers: the road and helicopter availabilities are the existing tables (see threatEffects.js); the drone, intensity and
 * artillery availabilities, D0 and K are EDUCATED GUESS (no study gives a fitted distribution: docs/research/RESEARCH_EVACUATION_DELAY.md); anchors:
 * Afghanistan permissive (75 percent of missions within 60 minutes, median 43 minutes call to facility), prolonged casualty care window 6 to 72
 * hours, Ukraine drone saturated: hours to days.
 */
import { resolveRoadState, helicopterAvailability } from '../../constants/threatEffects.js';
import { PFC_CAPABILITY, MEDICAL_CAPABILITY_TO_PFC } from '../../constants/prolongedFieldCare.js';
import { WEATHER_CONDITIONS } from '../../constants/operationsConfig.js';

// 2026-10-20 (W3): the weather lowers what drones can do (rain 0.6, snow 0.7, fog 0.4 ... in operationsConfig WEATHER_CONDITIONS.drone_effectiveness), so
// it shortens the share of time that drones keep a ground or air evacuation from moving: effective drone factor = 1 - (1 - drone factor) x effectiveness.
// EDUCATED GUESS (the effectiveness values are the existing table). false = the drone factor of the threat level alone, as before.
export const WEATHER_DRONE_EFFECT_ON = true;
const weatherDroneEffectiveness = (weather) => {
  if (!WEATHER_DRONE_EFFECT_ON || weather === undefined || weather === null) return 1;
  const w = WEATHER_CONDITIONS[weather];
  if (!w) throw new Error(`evacuationWindowModel: unknown weather '${weather}' (valid: ${Object.keys(WEATHER_CONDITIONS).join(', ')})`);
  return w.drone_effectiveness;
};
const droneFactor = (base, effectiveness) => 1 - (1 - base) * effectiveness;

export const EVAC_WINDOW_STATUS = Object.freeze({ status: 'EDUCATED_GUESS', basis: 'Road and helicopter availability: existing tables; drone, intensity, artillery, D0, K: guesses calibrated to the anchors in the header.' });

/** Share of the time a drone environment leaves a ground or air evacuation free to move. EDUCATED GUESS. */
export const DRONE_WINDOW = Object.freeze({ NONE: 1.0, RECONNAISSANCE_ONLY: 0.95, LIMITED_FPV: 0.85, CONTESTED: 0.6, SATURATED: 0.3 });
/** Fighting at the point of injury (enemy resistance of the phase). EDUCATED GUESS. */
export const INTENSITY_WINDOW = Object.freeze({ LIGHT: 1.0, MODERATE: 0.85, HEAVY: 0.65 });
/** Artillery on the collection and route area. EDUCATED GUESS. */
export const ARTILLERY_WINDOW = Object.freeze({ LOW: 1.0, MODERATE: 0.95, HIGH: 0.85, EXTREME: 0.7 });
/** Mean closure time (hours) of a window that is fully open (D0) and the growth with closure (K). EDUCATED GUESS. */
export const CLOSURE_D0_HOURS = 3;
export const CLOSURE_K = 4;
export const P_OPEN_FLOOR = 0.02;

const need = (table, key, name) => {
  if (!Object.prototype.hasOwnProperty.call(table, key)) throw new Error(`evacuationWindowModel: unknown ${name} '${key}' (valid: ${Object.keys(table).join(', ')})`);
  return table[key];
};

/** Share of the wait that prolonged field care takes off (only up to the hours the capability can sustain, PFC_CAPABILITY.max_hold_hours).
 *  NONE = the wait counts in full. EDUCATED GUESS (no study found); hours from the JTS prolonged casualty care guidelines. */
export const PFC_WAIT_OFFSET = Object.freeze({ NONE: 0, BASIC: 0.25, INTERMEDIATE: 0.5, ADVANCED: 0.7 });
export const WAIT_QUANTILE_POINTS = 10;

/** Wait (hours) as the casualty's wound curve sees it after prolonged field care. */
export function pfcEffectiveWait(waitHours, pfcKey, offsetScale = 1) {
  // offsetScale: Monte Carlo (R2-24d) scale of the PFC offset; the offset never reaches 1 (a wait never disappears)
  const offset = Math.min(0.95, need(PFC_WAIT_OFFSET, pfcKey, 'prolonged field care level') * offsetScale);
  const hold = PFC_CAPABILITY[pfcKey].max_hold_hours;
  return waitHours - offset * Math.min(waitHours, hold);
}

/** The PFC level of a Tab 1 medical capability (COLLAPSED, POOR, LIMITED, ADEQUATE, GOOD, EXCELLENT). */
export function pfcLevelOf(medicalCapability) {
  return need(MEDICAL_CAPABILITY_TO_PFC, medicalCapability, 'medical capability');
}

/**
 * The window from availabilities already resolved by the calculator (road state of the day, helicopter availability, drone level of the day).
 * @param {Object} c roadAvailability (null = no road effect), helicopterValue, droneLevel, artilleryLevel, resistanceLevel (LIGHT / MODERATE / HEAVY)
 */
export function evacuationWindowFromParts({ roadAvailability, helicopterValue, droneLevel, artilleryLevel, resistanceLevel, closureScale = 1, weather }) {
  const drone = droneFactor(need(DRONE_WINDOW, droneLevel || 'NONE', 'drone threat'), weatherDroneEffectiveness(weather));
  const fight = need(INTENSITY_WINDOW, resistanceLevel, 'resistance');
  const art = need(ARTILLERY_WINDOW, artilleryLevel || 'LOW', 'artillery threat');
  const pGround = Math.min(1, (roadAvailability === null || roadAvailability === undefined ? 1 : roadAvailability) * drone * fight * art);
  const pAir = Math.min(1, helicopterValue * drone * fight);
  return windowOf(pGround, pAir, { road: roadAvailability, helicopter: helicopterValue, drone, intensity: fight, artillery: art }, closureScale);
}

/** Wait values (hours) at the middle of each of n equal-probability slices of the wait distribution. */
export function waitSpreadPoints(win, n = WAIT_QUANTILE_POINTS) {
  const pts = [];
  for (let i = 0; i < n; i++) pts.push(win.quantile((i + 0.5) / n));
  return pts;
}

function windowOf(pGround, pAir, parts, closureScale = 1) {
  const pOpen = Math.max(P_OPEN_FLOOR, 1 - (1 - pGround) * (1 - pAir));
  const meanClosureHours = closureScale * CLOSURE_D0_HOURS * (1 + CLOSURE_K * (1 - pOpen));
  const quantile = (q) => (q <= pOpen ? 0 : -meanClosureHours * Math.log(1 - (q - pOpen) / (1 - pOpen)));
  const shareBeyond = (t) => (1 - pOpen) * Math.exp(-t / meanClosureHours);
  return {
    p_ground: pGround, p_air: pAir, p_open: pOpen, mean_closure_hours: meanClosureHours,
    mean_wait_hours: (1 - pOpen) * meanClosureHours,
    median_wait_hours: quantile(0.5), p75_wait_hours: quantile(0.75), p90_wait_hours: quantile(0.9),
    share_beyond_6h: shareBeyond(6), share_beyond_24h: shareBeyond(24),
    quantile, parts
  };
}

/**
 * @param {Object} c conditions: locConfig, threatAssessment (drone_threat, artillery_threat, enemy_ad, ...), weather, airSituation, resistance
 */
export function evacuationWindow({ locConfig, threatAssessment = {}, weather = 'CLEAR', airSituation = 'PARITY', resistance = 'MODERATE' }) {
  const drone = droneFactor(need(DRONE_WINDOW, threatAssessment.drone_threat || 'NONE', 'drone threat'), weatherDroneEffectiveness(weather));
  const fight = need(INTENSITY_WINDOW, resistance, 'resistance');
  const art = need(ARTILLERY_WINDOW, threatAssessment.artillery_threat || 'LOW', 'artillery threat');
  const road = resolveRoadState({ locConfig, threatAssessment, weather });
  const heli = helicopterAvailability({ airSituationLevel: airSituation, enemyAd: threatAssessment.enemy_ad || 'NONE', weather });
  const pGround = Math.min(1, road.availability * drone * fight * art);
  const pAir = Math.min(1, heli.value * drone * fight);
  return windowOf(pGround, pAir, { road: road.availability, helicopter: heli.value, drone, intensity: fight, artillery: art });
}

/** Evacuation environment (PERMISSIVE / CONTESTED / DENIED) that fits a probability that the window is open. EDUCATED GUESS thresholds. */
export const PARADIGM_FROM_P_OPEN = Object.freeze({ permissiveFrom: 0.8, contestedFrom: 0.3 });
export function suggestParadigm(pOpen) {
  if (typeof pOpen !== 'number' || Number.isNaN(pOpen)) throw new Error(`evacuationWindowModel.suggestParadigm: pOpen must be a number (got: ${pOpen})`);
  if (pOpen >= PARADIGM_FROM_P_OPEN.permissiveFrom) return 'PERMISSIVE';
  if (pOpen >= PARADIGM_FROM_P_OPEN.contestedFrom) return 'CONTESTED';
  return 'DENIED';
}
